import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  Alert, Box, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

import { fetchRequest as fetchBakeoff } from '../../redux/actions/discovery/fleetBakeoff';
import useRealtimeRefresh, { eventPrefix } from '../../realtime/useRealtimeRefresh';

const ENTRY_TONES = {
  pending: 'default', running: 'info', scored: 'success',
  failed: 'error', skipped: 'default',
};

const pct = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);

const FleetBakeoffCard = ({ goalId, bakeoff, bakeoffFetch }) => {
  const theme = useTheme();

  useEffect(() => { bakeoffFetch({ goalId }); }, [goalId, bakeoffFetch]);
  useRealtimeRefresh(
    eventPrefix('goal.fleet.bakeoff'),
    () => bakeoffFetch({ goalId }),
  );

  const doc = bakeoff?.response;
  if (bakeoff?.isLoading && !doc) {
    return <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={20} /></Stack>;
  }
  if (!doc) return null;

  const entries = [...(doc.entries || [])].sort(
    (a, b) => (b.score ?? -1) - (a.score ?? -1),
  );
  const winner = doc.winner;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <EmojiEventsIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          Model bake-off
        </Typography>
        {doc.status === 'running' && <Chip size="small" color="info" label="Running…" />}
        {doc.status === 'failed' && <Chip size="small" color="error" label="Failed" />}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Foundation models raced on a member sample. Best ranker wins.
      </Typography>

      {doc.status === 'failed' && doc.error && (
        <Alert severity="error" sx={{ mb: 1 }}>{String(doc.error)}</Alert>
      )}

      <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Model</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">
              <Tooltip title="Spearman correlation between the predicted ranking and what actually happened — 100% is a perfect ordering." arrow>
                <span style={{ cursor: 'help' }}>Rank agreement</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              <Tooltip title="Of the members the model put in the top 10%, how many actually ended up there." arrow>
                <span style={{ cursor: 'help' }}>Top-decile precision</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Blended</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((e) => {
            const isWinner = winner && e.family === winner.family && e.slug === winner.slug;
            return (
              <TableRow
                key={`${e.family}/${e.slug}`}
                sx={isWinner ? { bgcolor: alpha(theme.palette.warning.main, 0.07) } : undefined}
              >
                <TableCell>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {isWinner && <EmojiEventsIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />}
                    <Typography variant="body2" fontWeight={isWinner ? 700 : 400}>
                      {e.slug || e.family}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={ENTRY_TONES[e.status] || 'default'}
                    label={e.status}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                </TableCell>
                <TableCell align="right">{pct(e.metrics?.spearman)}</TableCell>
                <TableCell align="right">{pct(e.metrics?.top_decile_precision)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={isWinner ? 800 : 500}>
                    {e.score != null ? Number(e.score).toFixed(3) : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
          {!entries.length && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="caption" color="text.secondary">
                  No candidates evaluated yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </Box>
      {Boolean(doc.sample_size) && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Evaluated on a {doc.sample_size}-member sample.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  bakeoff: state.discovery_fleet_bakeoff,
});

const mapDispatchToProps = (dispatch) => ({
  bakeoffFetch: (payload) => dispatch(fetchBakeoff(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FleetBakeoffCard);
